import { Skeleton, Typography } from "@mui/material";
import * as React from "react";
import { SearchResultType } from "./constants";
import { convertCurrentTimestamp, formatTimestampToDate } from "../../helpers";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { setShowSearchModal } from "../../redux/slices/searchSlice";
import { v4 as uuidv4 } from "uuid";
import { highlightText } from "../ChatMessage/helpers";
import { logClickEvent } from "../../analytics";
import { useRefreshChatLastUpdatedNew } from "../Sidebar/hooks";

type SearchResultItemProps = {
  title?: string; // for rendering default chat titles
  sessionId?: string; // for rendering default chat titles
  searchResult?: SearchResultType;
  icon?: JSX.Element;
  resultRef?: React.Ref<HTMLDivElement>;
  isLoading?: boolean;
  searchedTerm?: string;
};
const SearchResultItem = ({ title, sessionId, searchResult, icon, resultRef, isLoading, searchedTerm }: SearchResultItemProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const chatSessionId = sessionId ?? searchResult?.session_id;
  const updateLastUpdatedDate = useRefreshChatLastUpdatedNew();

  const handleNavigation = () => {
    if (isLoading) return;
    dispatch(setShowSearchModal(false));
    if (chatSessionId) {
      const messageId = searchResult?.message_id; // navigating to a specific message
      const isBotResponse = searchResult?.matched_source === "bot_response";
      navigate(`/chat/${chatSessionId}`, {
        state: messageId ? { scrollToMessageId: isBotResponse ? messageId : `prompt-${messageId}`, searchedTerm } : null,
      });
      if (searchedTerm) {
        updateLastUpdatedDate.mutate({ sessionId: chatSessionId, title: searchResult?.session_title, date: convertCurrentTimestamp() });
        logClickEvent("search_result_click", { searchedTerm });
      }
    }
    else {
      const newChatId = uuidv4();
      navigate(`/chat/${newChatId}`);
    }
  };

  return (
    <div
      className={`flex p-[12px] mb-1 w-full items-center gap-x-3 hover:bg-tgpt-secondary-states-hover rounded-[4px] ${isLoading ? "cursor-default" : "cursor-pointer"}`}
      ref={resultRef}
      onClick={handleNavigation}
    >
      <>
        {isLoading ? <Skeleton animation="wave" variant="circular" width={16} height={16} className="!bg-tgpt-secondary-states-focus" /> : icon }
        <div className="flex flex-col flex-grow max-w-[250px] md:max-w-[434px]">
          {isLoading
            ? (
                <>
                  <Skeleton animation="wave" width={170} height={12} className="!bg-tgpt-secondary-states-focus" />
                  <div className="w-[276px] sm:w-[340px]">
                    <Skeleton animation="wave" height={12} className="!bg-tgpt-secondary-states-focus" />
                  </div>
                </>
              )
            : (
                <>
                  <div className="flex justify-between">
                    <Typography variant="body2/regular-400" className="matched-text truncate">{highlightText(searchResult?.session_title, searchedTerm, { jsx: true }) ?? title}</Typography>
                    <Typography variant="caption/regular-400" className="text-mui-black-23 dark:text-mui-white-38 hidden md:block whitespace-nowrap">{searchResult && formatTimestampToDate(searchResult?.created_at)}</Typography>
                  </div>
                  <Typography variant="caption" className="matched-text truncate">{highlightText(searchResult?.matched_sentence, searchedTerm, { jsx: true })}</Typography>
                </>
              )}
        </div>
      </>
    </div>
  );
};

export default SearchResultItem;
